import { useState } from 'react';
import ThemeCardBase from './ThemeCardBase';
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@atoms/new/dropdown';
import { type Theme, useThemeStore } from './store/ThemeStore';
import useThemesAPI from './hooks/useThemesAPI';
import SlideBackground from '@atoms/new/slideBackground';
import { PricingAlertNudgePopover } from '@components/PricingPlans/NudgesAlert';
import { useDisclosure } from '@heroui/react';
import useFonts from '@lib/hooks/useFonts';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { useRouter } from 'next/navigation';
import { DotsThreeOutlineVerticalIcon, PencilSimpleIcon, TrashSimpleIcon, CopyIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { usePurchasePlan } from '@lib/hooks/usePlanSettings';
interface ThemeCardBaseProps {
  onClick?: () => void;
  theme: Theme
  isDefault?: boolean;
  isSelected?: boolean;
  handleDefaultTheme?: (themeId: string) => void;
  openThemeModal?: () => void;
  onDelete?: (themeId: string) => void;
  height?: string;
  // presentation page related props
  isPresentationPage?: boolean;
  isStandardTheme?: boolean;
  showMenu?: boolean;
  minWidth?: string;
  isDisabled?: boolean;
  limitExceeded?: boolean;
  themeCustomizationAllowed?: boolean;
}
export default function ThemeCard({
  onClick,
  onDelete,
  theme,
  isDefault = false,
  isSelected = false,
  handleDefaultTheme,
  openThemeModal,
  isPresentationPage = false,
  isStandardTheme = false,
  showMenu = true,
  height,
  minWidth,
  isDisabled = false,
  limitExceeded = false,
  themeCustomizationAllowed = false
}: ThemeCardBaseProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { getFontStyle } = useFonts();
  const [, setStore] = useThemeStore(store => store.selectedTheme);
  const currentTeamBillingAccountOwnerID = useTeamsData((s) => s.currentTeamBillingAccountOwnerID);
  const { settings: planSettings, isOldPlan } = usePurchasePlan(currentTeamBillingAccountOwnerID);

  const { createThemeApi } = useThemesAPI();

  const onDuplicate = async () => {
    if (!theme) return;
    await createThemeApi(theme);
  };

  const {
    isOpen: isNewThemeUpgradeAlertOpen,
    onOpen: onNewThemeUpgradeAlertOpen,
    onClose: onNewThemeUpgradeAlertClose
  } = useDisclosure();

  const router = useRouter();

  return (
    <ThemeCardBase
      onClick={onClick}
      isSelected={isSelected}
      height='h-auto'
      className={isPresentationPage ? ' hover:border-red-500' : ''}
      minWidth={minWidth}
      isDisabled={isDisabled}
    >
      <div className='flex flex-col size-full relative overflow-hidden'>
        {isDefault && <div className='absolute text-xs font-medium bg-content3 text-content3-foreground h-5 px-1.5 rounded-[3px] z-2 flex items-center top-1 left-1'>Default</div>}
        <div
          style={{ background: theme.config.default.bgColor }}
          className='flex items-center justify-center relative aspect-video'
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {theme.config.default.imageSrc && (
            <SlideBackground
              bgImg={theme.config.default.imageSrc}
              bgImgOpacity={theme.config.default.imageOpacity / 100}
              thumbnail
              isHovered={isHovered}
            />
          )}
          <div className={`z-1 flex flex-col gap-2 px-4 w-full ${isPresentationPage ? 'mb-[0.5em]' : ''} `}>
            <p
              style={{
                color: theme.config.default.headingColor,
                ...getFontStyle(theme.config.default.headingFontFamily),
              }}
              className='text-lg text-left font-semibold'>Heading</p>
            <div
              style={{ background: theme.config.default.primaryColor ?? theme.config.colors.pollChartColor }}
              className='rounded-lg p-[5%] w-full text-left'
            >
              <p
                style={{
                  color: theme.config.default.textColor ?? theme.config.default.headingColor,
                  ...getFontStyle(theme.config.default.textFontFamily),
                }}
                className='text-sm'>Body</p>
            </div>
          </div>
        </div>
        <p className={`${isSelected ? 'bg-primary/20' : 'bg-content2'} ${showDropdown ? '' : ''} border-t border-divider font-semibold text-sm pl-4 pr-1.5 h-10 text-start flex justify-between items-center w-full overflow-hidden text-default-900`}>
          <span className='truncate flex-1 min-w-0'>
            {theme.name}
          </span>
          {showMenu && <Dropdown
            isOpen={showDropdown}
            closeOnSelect={false}
            onClose={() => {
              setShowDropdown(false);
            }}
            placement='bottom-start'>
            <DropdownTrigger>
              <span
                onClick={() => setShowDropdown(true)}
                className={`hover:bg-content3 rounded px-2 h-8 grid place-items-center cursor-pointer ${isDisabled ? 'cursor-not-allowed' : ''}`}
              >
                <DotsThreeOutlineVerticalIcon className='text-default-400' size={18} weight='fill' />
              </span>
            </DropdownTrigger>
            <DropdownMenu className='w-60'>
              <DropdownSection showDivider={(!isPresentationPage && isStandardTheme)}
                className="m-0!"
                classNames={{
                  divider: 'my-1'
                }}>
                <DropdownItem
                  startContent={<PencilSimpleIcon size={20} />}
                >
                  <div>
                    <PricingAlertNudgePopover
                      placement={isPresentationPage ? 'left-start' : 'right-start'}
                      offset={isPresentationPage ? 50 : 20}
                      crossOffset={-11}
                      isOpen={isNewThemeUpgradeAlertOpen}
                      onClose={onNewThemeUpgradeAlertClose}
                      title="Design your own themes"
                      onSuccess={() => {
                        onNewThemeUpgradeAlertClose();
                        router.push('/pricing');
                      }}
                      description="Create and apply your own themes. Upgrade to Pro."
                      trigger={(
                        <div
                          onClick={() => {
                            if (limitExceeded) {
                              onNewThemeUpgradeAlertOpen();
                              return;
                            }
                            if (!planSettings?.themeCustomizationAllowed) {
                              onNewThemeUpgradeAlertOpen();
                              return;
                            }
                            setShowDropdown(false);
                            if (!openThemeModal) return;
                            let name = theme.name;
                            if (!theme.is_editable) name = 'Untitled theme';
                            setStore({
                              selectedTheme: {
                                ...theme,
                                config: { ...theme.config },
                                name
                              }
                            });
                            openThemeModal();
                          }}
                        >
                          <p className='text-sm text-grey-900 dark:text-grey-200'>Customize this theme</p>

                          {isStandardTheme && <p className='text-sm text-grey-700 dark:text-grey-400 text-wrap mt-1'>Duplicate this theme to edit it for your workspace.</p>}
                        </div>
                      )}
                    />
                  </div>
                </DropdownItem>
              </DropdownSection>
              <DropdownSection
                className="m-0!"
                classNames={{
                  divider: 'my-1'
                }}
                hidden={isStandardTheme || (!isStandardTheme && !planSettings?.themeCustomizationAllowed)}
                showDivider={isPresentationPage && !isStandardTheme}>
                <DropdownItem
                  onClick={() => {
                    if (!planSettings?.themeCustomizationAllowed) {
                      setShowDropdown(false);
                      return;
                    }
                    if (limitExceeded) {
                      onNewThemeUpgradeAlertOpen();
                      return;
                    }
                    onDuplicate();
                    setShowDropdown(false);
                  }}
                  startContent={<CopyIcon size={20} />}
                >
                  Duplicate theme
                </DropdownItem>
              </DropdownSection>
              <DropdownSection
                className="m-0!"
                classNames={{
                  divider: 'my-1'
                }}
                hidden={isPresentationPage || (!isStandardTheme && !planSettings?.themeCustomizationAllowed)}
                showDivider={!isStandardTheme}>
                <DropdownItem
                  onClick={() => {
                    if (!isStandardTheme && !planSettings?.themeCustomizationAllowed) {
                      setShowDropdown(false);
                      return;
                    }
                    handleDefaultTheme?.(theme.id);
                    setShowDropdown(false);
                  }}
                  isDisabled={isDefault}
                  startContent={<CheckCircleIcon size={20} />}
                >
                  Set as default
                </DropdownItem>
              </DropdownSection>
              <DropdownSection className='mb-0'
                hidden={isStandardTheme}
              >
                <DropdownItem
                  onClick={() => {
                    onDelete?.(theme.id);
                    setShowDropdown(false);
                  }}
                  isDisabled={isStandardTheme}
                  startContent={<TrashSimpleIcon size={20} />}
                  className="text-danger!"
                >
                  Delete
                </DropdownItem>
              </DropdownSection>
            </DropdownMenu>
          </Dropdown>}

        </p>
      </div>
    </ThemeCardBase >
  );
}
